import { useCallback, useEffect, useState, useMemo } from "react";
import { type Marker, MarkerClusterer } from "@googlemaps/markerclusterer";
import {
  Map,
  useMap,
  AdvancedMarkerAnchorPoint,
  MapEvent,
  AdvancedMarker,
} from "@vis.gl/react-google-maps";

import {
  GetBrokerPropertiesLocationFilterInput,
  Property,
} from "gql/generated.graphql";

import {
  CARD_HEIGHT,
  CARD_WIDTH,
  DEFAULT_BOUND_DISTANCE,
  PIXEL_DISTANCE_FROM_MOUSE,
  Z_INDEX_HOVER,
  Z_INDEX_SELECTED,
} from "./constants/constants";

import { PropertyInfoWindow } from "./components/property-info-window";
import { PropertyMarker } from "./components/property-marker";
import { CustomClusterRenderer } from "./components/custom-cluster-renderer";
import { useScreenResolution } from "dash-ui";
import { IdxPreviousPageState } from "v2/pages/idx/idx-embedded-search-page";

type CustomProperty = Property & { zIndex: number };
type LatLng = { lat: number; lng: number };

type ExchangeMapViewProps = {
  properties: Property[];
  idxFeedId?: string;
  branding?: { mainColor?: string; textColor?: string };
  isLoadingProperties?: boolean;
  previousMapState?: { position?: LatLng; zoomLevel: number };
  currentState?: IdxPreviousPageState;
  setLocationFilters?: (
    value: GetBrokerPropertiesLocationFilterInput
  ) => Promise<void>;
};

export interface MobilePropertyDetailsNavigation {
  navigateToPropertyDetails: () => void;
}

export function ExchangeMapView(props: ExchangeMapViewProps) {
  const map = useMap();

  // The state of the map before clicking on a property
  const previousMapState = props.previousMapState;
  const previousCoordinates = previousMapState?.position;
  const previousZoomLevel = previousMapState?.zoomLevel;

  const {
    devices: { isMobile },
  } = useScreenResolution();

  const properties = useMemo(() => {
    if (props.properties.length === 0) {
      return props.properties as CustomProperty[];
    }

    const sortedProperties = [...props.properties].sort((a, b) => {
      const aLatitude = a.location.geoLocation.coordinates[1];
      const bLatitude = b.location.geoLocation.coordinates[1];
      return bLatitude - aLatitude;
    });

    // This is done to display the markers in a way that the ones with lower latitude
    // (higher zIndex) are on top of the ones with higher latitude (lower zIndex)
    const propertiesWithZIndex = sortedProperties.map((dataItem, index) => ({
      ...dataItem,
      zIndex: index,
    })) as CustomProperty[];

    return propertiesWithZIndex;
  }, [props.properties]);

  const [previousProperties, setPreviousProperties] = useState(properties);

  const firstProperty = useMemo(
    () => props.properties && props.properties[0],
    [props.properties]
  );

  const firstPropertyCenter = useMemo(
    () =>
      firstProperty
        ? {
            lat: firstProperty.location.geoLocation.coordinates[1],
            lng: firstProperty.location.geoLocation.coordinates[0],
          }
        : null,
    [firstProperty]
  );

  const miamiCenter = useMemo(() => {
    return { lat: 25.773427547769593, lng: -80.19023639965582 };
  }, []);

  const defaultCenter = useMemo(
    () => previousCoordinates ?? firstPropertyCenter ?? miamiCenter,
    [previousCoordinates, firstPropertyCenter, miamiCenter]
  );

  const calculateBounds = useMemo(
    () =>
      (
        center: LatLng,
        boundDistance: number
      ): { east: number; west: number; north: number; south: number } => {
        const currentCenter = center ?? defaultCenter;
        return {
          east: currentCenter.lng + boundDistance,
          west: currentCenter.lng - boundDistance,
          north: currentCenter.lat + boundDistance,
          south: currentCenter.lat - boundDistance,
        };
      },
    [defaultCenter]
  );

  const [isFirstRender, setIsFirstRender] = useState(false);
  const [currentPropertySearchBounds, setCurrentPropertySearchBounds] =
    useState(calculateBounds(defaultCenter, 0.1));

  const [hoverId, setHoverId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(
    null
  );

  const [markers, setMarkers] = useState<{ [key: string]: Marker }>({});

  const [mousePosition, setMousePosition] = useState<LatLng | null>(null);
  const [zoomLevel, setZoomLevel] = useState(previousZoomLevel ?? 13);

  const onMouseEnter = useCallback((id: string | null) => setHoverId(id), []);
  const onMouseLeave = useCallback(() => {
    setHoverId(null);
  }, []);
  const onMarkerClick = useCallback(
    (id: string | null) => {
      setSelectedId(id);
    },
    [selectedId]
  );

  const onMapClick = useCallback(() => {
    setSelectedId(null);
    setHoverId(null);
  }, []);

  useEffect(() => {
    if (
      properties.length > 0 ||
      (properties.length === 0 && !props.isLoadingProperties)
    ) {
      setPreviousProperties(properties);
    }
  }, [properties, props.isLoadingProperties]);

  // Update selected property when selectedId changes
  useEffect(() => {
    const selectedPropertyId = isMobile ? selectedId : hoverId;
    if (selectedPropertyId) {
      // Between renders and filtering, the properties can be empty for a short period of time,
      // so we use the previous properties to find the selected property
      const propertyList =
        properties.length > 0 ? properties : previousProperties;
      const propertyInfo = propertyList.find(
        (property) => property._id === selectedPropertyId
      );
      setSelectedProperty(propertyInfo ?? null);
    } else {
      setSelectedProperty(null);
    }
  }, [hoverId, selectedId, properties, previousProperties]);

  // This is the first render, so for optimization purposes, we set the bounds to a smaller area
  // for user interaction, while he waits for the rest of the properties to load.
  // This can probably be changed once we optimize the database and the backend query
  useEffect(() => {
    if (props.setLocationFilters && firstPropertyCenter) {
      const newBounds = calculateBounds(firstPropertyCenter, 0.1);
      props.setLocationFilters({
        lat: firstPropertyCenter.lat,
        lng: firstPropertyCenter.lng,
        bounds: newBounds,
      });
      setIsFirstRender(true);
      setCurrentPropertySearchBounds(newBounds);
    }
  }, []);

  const havePropertiesChanged = (
    properties: Property[],
    previousProperties: Property[]
  ): boolean => {
    return (
      properties.length !== previousProperties.length ||
      properties.some(
        (property, index) => property._id !== previousProperties[index]._id
      )
    );
  };

  // This is our background query to search for properties in a bigger area after the first
  // render where we gave the user a smaller area to interact with
  useEffect(() => {
    if (isFirstRender) {
      if (
        properties.length > 0 &&
        (havePropertiesChanged(properties, previousProperties) ||
          previousMapState)
      ) {
        if (props.setLocationFilters && firstPropertyCenter) {
          const newBounds = calculateBounds(
            firstPropertyCenter,
            DEFAULT_BOUND_DISTANCE
          );

          props.setLocationFilters({
            lat: firstPropertyCenter.lat,
            lng: firstPropertyCenter.lng,
            bounds: newBounds,
          });

          setIsFirstRender(false);
          setCurrentPropertySearchBounds(newBounds);
        }
      }
    }
  }, [properties]);

  const updateMapBounds = useCallback(
    (ev: MapEvent) => {
      const cameraBounds = ev.map?.getBounds();

      // Set camera bounds with fallback to infinity values to ensure bounds update logic triggers
      // even if camera bounds are unavailable, maintaining robust map interaction.
      const cameraBoundsWest =
        cameraBounds?.getSouthWest().lng() ?? Number.NEGATIVE_INFINITY;
      const cameraBoundsSouth =
        cameraBounds?.getSouthWest().lat() ?? Number.NEGATIVE_INFINITY;
      const cameraBoundsEast =
        cameraBounds?.getNorthEast().lng() ?? Number.POSITIVE_INFINITY;
      const cameraBoundsNorth =
        cameraBounds?.getNorthEast().lat() ?? Number.POSITIVE_INFINITY;

      const cameraCenterMap = ev.map?.getCenter();
      const cameraCenter = {
        lat: cameraCenterMap?.lat() ?? defaultCenter.lat,
        lng: cameraCenterMap?.lng() ?? defaultCenter.lng,
      };

      if (!props.isLoadingProperties) {
        if (
          cameraBoundsEast > currentPropertySearchBounds?.east ||
          cameraBoundsWest < currentPropertySearchBounds?.west ||
          cameraBoundsNorth > currentPropertySearchBounds?.north ||
          cameraBoundsSouth < currentPropertySearchBounds?.south
        ) {
          const newBounds = calculateBounds(
            cameraCenter,
            DEFAULT_BOUND_DISTANCE
          );
          setCurrentPropertySearchBounds(newBounds);

          if (props.setLocationFilters) {
            props.setLocationFilters({
              lat: cameraCenter.lat ?? defaultCenter.lat,
              lng: cameraCenter.lng ?? defaultCenter.lng,
              bounds: newBounds,
            });
          }
        }
      }
      setSelectedId(null);
    },
    [props.isLoadingProperties, currentPropertySearchBounds]
  );

  const currentProperties = useMemo(() => {
    return properties?.length === 0 && props.isLoadingProperties
      ? previousProperties
      : properties;
  }, [properties, props.isLoadingProperties, previousProperties]);

  const clusterer = useMemo(() => {
    if (!map) return null;
    return new MarkerClusterer({ map, renderer: new CustomClusterRenderer() });
  }, [map]);

  useEffect(() => {
    if (!clusterer) return;

    clusterer.clearMarkers();
    clusterer.addMarkers(Object.values(markers));
  }, [clusterer, markers]);

  const setMarkerRef = useCallback((marker: Marker | null, key: string) => {
    setMarkers((markers) => {
      if ((marker && markers[key]) || (!marker && !markers[key]))
        return markers;

      if (marker) {
        return { ...markers, [key]: marker };
      } else {
        const { [key]: _, ...newMarkers } = markers;

        return newMarkers;
      }
    });
  }, []);

  // Add mousemove event listener to the map so that we can display the property
  // card on desktop using the position of the mouse as a reference
  useEffect(() => {
    if (!map) return;

    const handleMouseMove = (event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        setMousePosition({
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        });
      }
    };

    map.addListener("mousemove", handleMouseMove);

    return () => {
      google.maps.event.clearListeners(map, "mousemove");
    };
  }, [map]);

  const onZoomChanged = useCallback(() => {
    if (map) {
      const newZoomLevel = map.getZoom();
      if (newZoomLevel) {
        setZoomLevel(newZoomLevel);
      }

      // Cleans selected marker on zoom
      setSelectedId(null);
      setHoverId(null);
    }
  }, [map]);

  // Computes geographic offsets for a given pixel distance from the map center, adjusting for zoom level.
  const calculateOffset = useCallback(
    (map: google.maps.Map, pixelDistance: number) => {
      // Get the map's projection and center point
      const projection = map.getProjection();
      const centerLatLng = map.getCenter();

      // Return zero offsets if projection or center is not available
      if (!projection || !centerLatLng) return { latOffset: 0, lngOffset: 0 };

      // Calculate the scale based on the current zoom level
      const scale = Math.pow(2, zoomLevel);

      // Convert the center LatLng to world coordinates
      const worldCoordinateCenter = projection.fromLatLngToPoint(centerLatLng);
      if (!worldCoordinateCenter) return { latOffset: 0, lngOffset: 0 };

      // Calculate the pixel offset based on the scale
      const pixelOffset = pixelDistance / scale;

      // Convert the world coordinates back to LatLng with the pixel offset applied
      const offsetLatLng = projection.fromPointToLatLng(
        new google.maps.Point(
          worldCoordinateCenter.x,
          worldCoordinateCenter.y - pixelOffset
        )
      );

      // Return zero offsets if conversion fails
      if (!offsetLatLng) return { latOffset: 0, lngOffset: 0 };

      // Calculate the latitude and longitude offsets
      return {
        latOffset: offsetLatLng.lat() - centerLatLng.lat(),
        lngOffset: offsetLatLng.lng() - centerLatLng.lng(),
      };
    },
    [zoomLevel]
  );

  const propertyInfoWindowPosition = useMemo(() => {
    if (!map || !mousePosition) return undefined;
    const { latOffset, lngOffset } = calculateOffset(
      map,
      PIXEL_DISTANCE_FROM_MOUSE
    );
    return {
      lat: mousePosition.lat + latOffset,
      lng: mousePosition.lng + lngOffset,
    };
  }, [map, mousePosition, calculateOffset]);

  // Adjusts the position to display the info window on the top or bottom, and to
  // the right or left, depending on the position of the mouse and bound limits
  const adjustPropertyInfoWindowPosition = useCallback(() => {
    if (!map || !propertyInfoWindowPosition || !mousePosition)
      return propertyInfoWindowPosition;

    const bounds = map.getBounds();
    if (!bounds) return propertyInfoWindowPosition;

    const { lat, lng } = propertyInfoWindowPosition;
    const centerLat =
      (bounds.getNorthEast().lat() + bounds.getSouthWest().lat()) / 2;

    let adjustedLat = lat;
    let adjustedLng = lng;

    // Calculate the scale based on the current zoom level
    const scale = Math.pow(2, zoomLevel);

    // Determine if the mousePosition is above or below the center of the map
    if (mousePosition.lat > centerLat) {
      // Mouse is above the center, show card below
      adjustedLat = mousePosition.lat - CARD_HEIGHT / scale;
    } else {
      // Mouse is below the center, show card just above the marker
      adjustedLat = propertyInfoWindowPosition.lat;
    }

    // Ensure the card does not go beyond the right and left borders
    if (lng + CARD_WIDTH / scale > bounds.getNorthEast().lng()) {
      adjustedLng = bounds.getNorthEast().lng() - CARD_WIDTH / scale;
    } else if (lng - CARD_WIDTH / scale < bounds.getSouthWest().lng()) {
      adjustedLng = bounds.getSouthWest().lng() + CARD_WIDTH / scale;
    }

    return { lat: adjustedLat, lng: adjustedLng };
  }, [map, propertyInfoWindowPosition, mousePosition, zoomLevel]);

  // When Mobile, the marker will set the navigation information
  const [mobilePropertyNavigation, setMobilePropertyNavigation] =
    useState<MobilePropertyDetailsNavigation | null>(null);

  // Mobile: card fixed at the bottom of the map
  // Desktop: card follows mouse position on the map
  const renderPropertyInfoWindow = () => {
    if (selectedProperty) {
      if (isMobile) {
        // Calculate a position that simulates the bottom of the map
        const bounds = map?.getBounds();
        const southLat = bounds
          ? bounds.getSouthWest().lat()
          : defaultCenter.lat;

        const center = map?.getCenter();
        const centerLng = center ? center?.lng() : defaultCenter.lng;

        return (
          <AdvancedMarker
            position={{
              lat: southLat,
              lng: centerLng,
            }}
            zIndex={Z_INDEX_HOVER}
            anchorPoint={AdvancedMarkerAnchorPoint["CENTER_TOP"]}
            onClick={mobilePropertyNavigation?.navigateToPropertyDetails}
          >
            <div className="bottom-0 z-50 bg-white shadow-lg mb-6 rounded-lg">
              <PropertyInfoWindow
                idxFeedId={props.idxFeedId}
                property={selectedProperty}
                branding={props.branding}
              />
            </div>
          </AdvancedMarker>
        );
      } else {
        return (
          <AdvancedMarker
            position={adjustPropertyInfoWindowPosition()}
            zIndex={Z_INDEX_HOVER}
          >
            <PropertyInfoWindow
              idxFeedId={props.idxFeedId}
              property={selectedProperty}
              branding={props.branding}
            />
          </AdvancedMarker>
        );
      }
    }
  };

  return (
    <Map
      className="h-full w-full"
      defaultCenter={defaultCenter}
      defaultZoom={zoomLevel}
      minZoom={10}
      maxZoom={18}
      mapId="6e088950855b349d"
      mapTypeId="roadmap"
      onClick={onMapClick}
      onDragend={updateMapBounds}
      onZoomChanged={onZoomChanged}
      clickableIcons={false}
      streetViewControl={false}
      zoomControl={isMobile ? false : true}
    >
      {currentProperties?.map((property) => {
        const id = property._id;
        let zIndex = property.zIndex;
        const isHovered = hoverId === id;

        if (isHovered) {
          zIndex = Z_INDEX_HOVER;
        }

        if (selectedId === id) {
          zIndex = Z_INDEX_SELECTED;
        }
        return (
          <PropertyMarker
            onMarkerClick={() => onMarkerClick(id)}
            setMobilePropertyNavigation={setMobilePropertyNavigation}
            onMouseEnter={() => onMouseEnter(id)}
            onMouseLeave={onMouseLeave}
            property={property}
            isSelected={selectedId === id}
            branding={props.branding}
            idxFeedId={props.idxFeedId}
            key={id}
            style={{
              transformOrigin: AdvancedMarkerAnchorPoint["CENTER"].join(" "),
              transition: "transform 0.2s ease-in-out",
              ...(isHovered && { opacity: 1.0 }),
            }}
            zIndex={zIndex}
            setMarkerRef={setMarkerRef}
            isHovered={isHovered}
            isMobile={isMobile}
            zoomLevel={zoomLevel}
            currentState={props?.currentState}
          />
        );
      })}
      {(hoverId || selectedId) &&
        selectedProperty &&
        renderPropertyInfoWindow()}
    </Map>
  );
}
